import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

// Dismissal is keyed by the anchor (to_order) machine's id, not by a specific alternative
// pairing: once you say "geen interesse" for a machine, none of its suggested alternatives
// are shown again, even if the set of alternatives changes later.
export function useDismissedExtensionSuggestions(userId: string | null) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const loadDismissed = async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('dismissed_extension_suggestions')
        .select('machine_id')
        .eq('user_id', userId);
      if (data) setDismissedIds(new Set(data.map((row) => row.machine_id)));
    } catch {
      // Non-critical: an empty set is a safe fallback when dismissals can't be loaded
    }
  };

  useEffect(() => {
    loadDismissed();
  }, [userId]);

  async function dismiss(anchorMachineId: string): Promise<boolean> {
    if (!userId) return false;
    const snapshot = dismissedIds;
    setDismissedIds((prev) => new Set(prev).add(anchorMachineId));
    try {
      const { error } = await supabase
        .from('dismissed_extension_suggestions')
        .upsert({ user_id: userId, machine_id: anchorMachineId }, { onConflict: 'user_id,machine_id' });
      if (error) throw error;
      return true;
    } catch {
      setDismissedIds(snapshot);
      return false;
    }
  }

  async function reset(): Promise<boolean> {
    if (!userId) return false;
    const snapshot = dismissedIds;
    setDismissedIds(new Set());
    try {
      const { error } = await supabase.from('dismissed_extension_suggestions').delete().eq('user_id', userId);
      if (error) throw error;
      return true;
    } catch {
      setDismissedIds(snapshot);
      return false;
    }
  }

  return { dismissedIds, dismiss, reset };
}
